// Small helpers for in-app sound + OS-level notifications on ticket events.
// Duplicated in apps/pos/src/lib/notify.ts — the two apps don't share a
// package, and this is small enough not to warrant one.
import { getAuthToken, syncApiUrl } from "@cliqsuite/db/powersync";
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging, type PermissionStatus } from "@capacitor-firebase/messaging";

// The installed app (Android, iOS) has no Web Notifications: it asks and pushes through Firebase (FCM),
// which hands out a Firebase token on both platforms (on iOS through the APNs key uploaded to Firebase).
const native = Capacitor.isNativePlatform();
const fromNative = (p: PermissionStatus): NotificationStatus => (p.receive === "granted" || p.receive === "denied" ? p.receive : "default");

// Sounds play through Web Audio, not an <audio> element: on iPhone an <audio> element counts as media,
// so iOS showed a music player for the chime. Web Audio is a sound effect. The browser only lets it start
// after a tap, so any tap wakes it (and wakes it again after the app was in the background).
let ctx: AudioContext | null = null;
const buffers = new Map<string, Promise<AudioBuffer>>();

function audio(): AudioContext {
  if (!ctx) {
    // iOS: "playback" rings with the mute switch on, which a kitchen screen needs (Safari 16.4+; ignored elsewhere)
    const session = (navigator as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "playback";
    ctx = new AudioContext();
  }
  return ctx;
}

function load(src: string): Promise<AudioBuffer> {
  let buffer = buffers.get(src);
  if (!buffer) {
    buffer = fetch(src)
      .then((res) => res.arrayBuffer())
      .then((data) => audio().decodeAudioData(data));
    buffers.set(src, buffer);
  }
  return buffer;
}

function wake() {
  if (ctx?.state !== "running") void audio().resume().catch(() => {});
}
document.addEventListener("pointerdown", wake, { capture: true, passive: true });
// iOS pauses it while the app is in the background; try again as soon as it is back in front
document.addEventListener("visibilitychange", () => !document.hidden && ctx && wake());

/** From the alerts button's tap: wakes the sound and loads the chime, so a ticket arriving later can ring. */
export function unlockAudio(): void {
  wake();
  void load("/sounds/new-ticket.mp3").catch(() => {});
}

export function playSound(src: string) {
  const c = audio();
  void c.resume().catch(() => {});
  void load(src)
    .then((buffer) => {
      const source = c.createBufferSource();
      source.buffer = buffer;
      source.connect(c.destination);
      source.start();
    })
    .catch(() => {
      // Not woken by a tap yet this session: the ticket's glow still lands the point.
    });
}

export type NotificationStatus = "granted" | "denied" | "default" | "unsupported";

export function getNotificationStatus(): NotificationStatus {
  if (native) return "default"; // the real answer is async: checkNotificationStatus
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export async function checkNotificationStatus(): Promise<NotificationStatus> {
  return native ? fromNative(await FirebaseMessaging.checkPermissions()) : getNotificationStatus();
}

export async function requestNotificationPermission(): Promise<NotificationStatus> {
  if (native) return fromNative(await FirebaseMessaging.requestPermissions());
  if (typeof Notification === "undefined") return Promise.resolve("unsupported");
  if (Notification.permission !== "default") return Promise.resolve(Notification.permission);
  return Notification.requestPermission();
}

// Not a secret — this is meant to ship in the browser bundle. Paired with
// VAPID_PRIVATE_KEY on sync-api (see services/sync-api/src/lib/webpush.ts);
// if you ever rotate these, update both places (see .env.production.example).
const VAPID_PUBLIC_KEY = "BDFxPS9HabykC91SXTgh-QDVcRJMytnHSusG1vXmXLrtDpZjuVPsGcdPOQt5mYn2QNO5mfmCYHjqHMwXrNsyUPc";

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Subscribes this device to real Web Push (via the service worker's own
 * push/notificationclick handlers in sw.ts) and registers the subscription
 * with sync-api against this device's id — this is what lets a "new
 * ticket" alert land even when the PWA is fully closed, not just
 * backgrounded, since the OS wakes the service worker independently of
 * whether the page is running. Requires Notification permission already
 * granted; safe to call on every mount (subscribing when already
 * subscribed just returns the existing subscription — no re-prompt, no
 * duplicate).
 */
export async function subscribeToPush(deviceId: string): Promise<void> {
  if (native) return registerNative();
  if (Notification.permission !== "granted") return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await fetch(syncApiUrl("/push/subscribe"), {
      method: "POST",
      headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
      body: JSON.stringify({ deviceId, ...subscription.toJSON() }),
    });
  } catch (err) {
    console.error("Push subscribe failed:", err);
  }
}

let nativeRegistered = false;

/** The installed app: its Firebase token, sent to sync-api for this device (the device comes from the token). */
async function registerNative(): Promise<void> {
  if (nativeRegistered || (await FirebaseMessaging.checkPermissions()).receive !== "granted") return;
  nativeRegistered = true;
  const send = (token: string) =>
    fetch(syncApiUrl("/push/subscribe-fcm"), {
      method: "POST",
      headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
      body: JSON.stringify({ token }),
    }).catch((err) => console.error("Push subscribe failed:", err));
  try {
    // Android: the channel sync-api's pushes name; high importance so a ticket sounds and pops up
    if (Capacitor.getPlatform() === "android") {
      await FirebaseMessaging.createChannel({ id: "tickets", name: "New tickets", importance: 5, sound: "default", vibration: true, visibility: 1 });
    }
    await FirebaseMessaging.removeAllListeners();
    await FirebaseMessaging.addListener("tokenReceived", ({ token }) => void send(token)); // Firebase can rotate it
    await send((await FirebaseMessaging.getToken()).token);
  } catch (err) {
    nativeRegistered = false;
    console.error("Push subscribe failed:", err);
  }
}

// Alerts can be turned off on this display even once the OS allows them (the OS permission can't be
// taken back from inside the app): no chime, and sync-api stops sending to it.
const ALERTS_OFF_KEY = "cliqsuite_kds_alerts_off";

export function alertsOff(): boolean {
  try {
    return localStorage.getItem(ALERTS_OFF_KEY) === "1";
  } catch {
    return false;
  }
}

export async function setAlertsOff(off: boolean, deviceId: string): Promise<void> {
  try {
    if (off) localStorage.setItem(ALERTS_OFF_KEY, "1");
    else localStorage.removeItem(ALERTS_OFF_KEY);
  } catch {
    // no storage: it only lasts until the app restarts
  }
  if (!off) return subscribeToPush(deviceId);
  nativeRegistered = false; // turning back on registers again
  await fetch(syncApiUrl("/push/unsubscribe"), {
    method: "POST",
    headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
    body: "{}",
  }).catch((err) => console.error("Push unsubscribe failed:", err));
}
